import React, { useMemo, useState } from "react";
import { ShieldCheck, Shield, Ban, Repeat, StopCircle, LineChart, Lock } from "lucide-react";
import Reveal from "../components/common/Reveal";

const PRINCIPLES = [
  { icon: Shield, title: "Protect Capital", desc: "Your first job is survival. Keep losses small so you can trade another day." },
  { icon: Lock, title: "Fixed Risk", desc: "Risk the same fixed percentage every trade — never size up on 'certainty'." },
  { icon: Ban, title: "Avoid Revenge Trading", desc: "Never chase a loss with a bigger trade. The market owes you nothing." },
  { icon: Repeat, title: "Avoid Overtrading", desc: "More trades ≠ more profit. Wait for setups that fit your plan." },
  { icon: StopCircle, title: "Daily Loss Limit", desc: "Set a maximum daily loss before you start, and honour it." },
  { icon: LineChart, title: "Review Performance", desc: "Study your journal weekly. Improvement comes from reflection." },
];

export default function RiskManagement() {
  const [capital, setCapital] = useState(1000);
  const [riskPct, setRiskPct] = useState(1);
  const [maxDailyPct, setMaxDailyPct] = useState(3);
  const [trades, setTrades] = useState(3);
  const [winRate, setWinRate] = useState(58);

  const c = Number(capital) || 0;
  const perTrade = c * (Number(riskPct) || 0) / 100;
  const maxDaily = c * (Number(maxDailyPct) || 0) / 100;
  const allocation = trades > 0 ? maxDaily / trades : 0;
  const planned = perTrade * (Number(trades) || 0);
  const remaining = Math.max(0, maxDaily - planned);

  const PAYOUT = 0.85;
  const edge = (Number(winRate) / 100) * PAYOUT - (1 - Number(winRate) / 100);
  const breakeven = Math.round((1 / (1 + PAYOUT)) * 100);
  const projected = c * Math.pow(1 + (Number(riskPct) / 100) * edge, 30);

  const meter = useMemo(() => Math.min(100, Math.round(((Number(riskPct) || 0) / 5) * 100)), [riskPct]);
  const meterTone = meter < 40 ? "#8fd6b4" : meter < 70 ? "#e6b450" : "#e0949c";
  const meterLabel = meter < 40 ? "Conservative" : meter < 70 ? "Moderate" : "Aggressive";

  const results = [
    ["Max risk / trade", `$${perTrade.toFixed(2)}`],
    ["Max daily risk", `$${maxDaily.toFixed(2)}`],
    ["Suggested allocation / trade", `$${allocation.toFixed(2)}`],
    ["Remaining daily risk", `$${remaining.toFixed(2)}`],
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <Reveal>
        <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500">
          <ShieldCheck size={14} /> Capital Protection
        </div>
        <h1 className="font-head text-2xl font-bold uppercase tracking-tight text-white md:text-3xl">Risk Management</h1>
      </Reveal>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        {/* calculator */}
        <Reveal className="lg:col-span-3">
          <div className="surface rounded-2xl p-6 md:p-8">
            <div className="grid gap-5 sm:grid-cols-2">
              <Num label="Account capital ($)" value={capital} onChange={setCapital} testid="risk-capital" />
              <Num label="Planned trades" value={trades} onChange={setTrades} testid="risk-trades" />
              <Range label="Risk per trade" value={riskPct} onChange={setRiskPct} testid="risk-pertrade" />
              <Range label="Max daily risk" value={maxDailyPct} onChange={setMaxDailyPct} max={10} testid="risk-daily" />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              {results.map(([l, v]) => (
                <div key={l} className="surface-2 rounded-xl p-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{l}</div>
                  <div className="mt-1 font-head text-xl font-bold tabular-nums text-white">{v}</div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <div className="mb-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest">
                <span className="text-neutral-500">Risk exposure</span>
                <span style={{ color: meterTone }}>{meterLabel}</span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/6">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${meter}%`, background: meterTone }} data-testid="risk-meter" />
              </div>
            </div>

            <div className="mt-6 rounded-xl border-l-2 border-white/20 bg-white/[0.02] p-4 text-sm leading-relaxed text-neutral-400">
              Risk management controls your <span className="text-white">exposure</span>, not your outcome. It cannot turn a
              losing strategy into a profitable one, and no setting guarantees profit. Trading involves risk; past
              performance does not guarantee future results.
            </div>
          </div>
        </Reveal>

        {/* summary */}
        <Reveal delay={0.05} className="lg:col-span-2">
          <div className="surface flex h-full flex-col rounded-2xl p-6 md:p-8">
            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Example</div>
            <p className="mt-3 text-sm leading-relaxed text-neutral-400">
              With <span className="text-white">${c.toLocaleString()}</span> capital at
              <span className="text-white"> {riskPct}%</span> risk per trade, your maximum risk on a single trade is
              <span className="font-mono text-white"> ${perTrade.toFixed(2)}</span>. This is the amount you accept losing —
              not a projected gain.
            </p>
            <div className="mt-auto space-y-2 pt-6">
              <Row k="Daily stop" v={`$${maxDaily.toFixed(2)}`} />
              <Row k="Trades planned" v={trades} />
              <Row k="Left after plan" v={`$${remaining.toFixed(2)}`} />
            </div>
          </div>
        </Reveal>
      </div>

      {/* projection */}
      <Reveal delay={0.05}>
        <div className="surface relative mt-6 overflow-hidden rounded-2xl p-6 md:p-8">
          <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Discipline projection · illustrative</div>
              <h2 className="mt-1 font-head text-lg font-bold uppercase tracking-tight text-white">30-trade equity path</h2>
            </div>
            <div className="w-full max-w-xs">
              <span className="mb-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                <span>Assumed win rate</span><span className="text-white">{winRate}%</span>
              </span>
              <input type="range" min="30" max="80" step="1" value={winRate} onChange={(e) => setWinRate(e.target.value)} data-testid="risk-winrate" className="w-full accent-[#d4d8dc]" />
            </div>
          </div>

          <EquityCurve capital={c} riskPct={Number(riskPct)} winRate={Number(winRate)} />

          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3">
            <div className="surface-2 rounded-xl p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Break-even win rate</div>
              <div className="mt-1 font-head text-xl font-bold tabular-nums text-white">{breakeven}%</div>
              <div className="mt-1 text-[11px] text-neutral-500">at 85% payout</div>
            </div>
            <div className="surface-2 rounded-xl p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Edge per trade</div>
              <div className={`mt-1 font-head text-xl font-bold tabular-nums ${edge >= 0 ? "text-[#8fd6b4]" : "text-[#e0949c]"}`}>{edge >= 0 ? "+" : ""}{(edge * 100).toFixed(1)}%</div>
              <div className="mt-1 text-[11px] text-neutral-500">expected value</div>
            </div>
            <div className="surface-2 col-span-2 rounded-xl p-4 md:col-span-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Projected after 30</div>
              <div className={`mt-1 font-head text-xl font-bold tabular-nums ${projected >= c ? "text-[#8fd6b4]" : "text-[#e0949c]"}`}>${projected.toFixed(0)}</div>
              <div className="mt-1 text-[11px] text-neutral-500">from ${c.toFixed(0)} capital</div>
            </div>
          </div>
          <p className="mt-4 text-[11px] leading-relaxed text-neutral-600">
            Illustrative only. A positive edge does not guarantee profit — variance means real results swing above and below this path. Trading involves risk.
          </p>
        </div>
      </Reveal>

      {/* principles */}
      <div className="mt-10">
        <Reveal><h2 className="mb-5 font-head text-lg font-bold uppercase tracking-tight text-white">Discipline principles</h2></Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.04}>
              <div className="surface rounded-2xl p-6">
                <span className="mb-4 grid h-10 w-10 place-items-center rounded-xl border border-white/10 text-[#d4d8dc]"><p.icon size={17} /></span>
                <h3 className="font-head text-sm font-bold uppercase tracking-tight text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">{p.desc}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.28}>
            <div className="surface flex h-full items-center gap-4 rounded-2xl p-6">
              <span className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 text-[#e0949c]"><StopCircle size={17} /></span>
              <div>
                <h3 className="font-head text-sm font-bold uppercase tracking-tight text-white">Stop at the limit</h3>
                <p className="mt-1 text-sm text-neutral-400">When the daily loss limit is reached, stop. No exceptions.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

function Num({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-sm text-white outline-none focus:border-[#d4d8dc]/60" />
    </label>
  );
}
function Range({ label, value, onChange, max = 5, testid }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        <span>{label}</span><span className="text-white">{Number(value).toFixed(1)}%</span>
      </span>
      <input type="range" min="0.1" max={max} step="0.1" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full accent-[#d4d8dc]" />
    </label>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between border-t border-white/6 py-2.5">
      <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{k}</span>
      <span className="font-mono text-sm font-semibold tabular-nums text-white">{v}</span>
    </div>
  );
}

function EquityCurve({ capital, riskPct, winRate }) {
  const PAYOUT = 0.85;
  const edge = (winRate / 100) * PAYOUT - (1 - winRate / 100);
  const pts = [];
  let bal = capital;
  for (let i = 0; i <= 30; i++) {
    pts.push(bal);
    bal = bal * (1 + (riskPct / 100) * edge);
  }
  const min = Math.min(...pts, capital), max = Math.max(...pts, capital * 1.02);
  const W = 800, H = 180;
  const coords = pts.map((v, i) => {
    const x = (i / 30) * W;
    const y = H - ((v - min) / (max - min || 1)) * (H - 20) - 10;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const up = edge >= 0;
  const color = up ? "#8fd6b4" : "#e0949c";
  return (
    <div className="relative overflow-hidden rounded-xl border border-white/8 bg-black/20 p-3">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-40 w-full" data-testid="equity-curve">
        <defs>
          <linearGradient id="eqFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((g) => <line key={g} x1="0" y1={H * g} x2={W} y2={H * g} stroke="rgba(255,255,255,0.05)" />)}
        <polygon points={`0,${H} ${coords.join(" ")} ${W},${H}`} fill="url(#eqFill)" />
        <polyline points={coords.join(" ")} fill="none" stroke={color} strokeWidth="2" className="chart-line" style={{ strokeDasharray: "none" }} />
      </svg>
    </div>
  );
}
